import React, { useState } from "react";
import {
  IconPlaylist,
  IconDownload,
  IconPrinter,
  IconGift,
  IconTrophy,
  IconUsers,
  IconChartPie,
  IconRefresh,
  IconChartBar,
} from "@tabler/icons";
import {
  Button,
  Paper,
  Group,
  Stack,
  Divider,
  Box,
  Text,
  Table,
  Badge,
  Progress,
  Grid,
  Card,
  LoadingOverlay,
  ActionIcon,
  Center,
  RingProgress,
  SimpleGrid,
} from "@mantine/core";
import { useFormatter } from "../hooks/useFomatter";
import moment from "moment";
import _ from "lodash";

// Dummy data for demonstration
const DUMMY_CAMPAIGN_DATA = {
  totalAgents: 15,
  runDate: new Date(),
  status: "COMPLETED", // DRAFT, RUNNING, COMPLETED, ENDED
  metrics: {
    totalPremium: 1542000,
    totalFYC: 285000,
    averagePerAgent: 102800,
  },
  tierStats: [
    {
      tierName: "Gold Elite",
      chineseName: "金牌菁英",
      achieved: 2,
      percentage: 13.3,
      color: "orange",
    },
    {
      tierName: "Silver Champion",
      chineseName: "銀牌冠軍",
      achieved: 3,
      percentage: 20.0,
      color: "orange",
    },
    {
      tierName: "Bronze Star",
      chineseName: "銅牌之星",
      achieved: 5,
      percentage: 33.3,
      color: "orange",
    },
    {
      tierName: "No Reward",
      chineseName: "未達標",
      achieved: 5,
      percentage: 33.3,
      color: "orange",
    },
  ],
  agentResults: [
    // Gold Elite (2 agents - 13.3%)
    {
      agentId: "AG001",
      agentName: "張大明",
      englishName: "David Zhang",
      metrics: {
        totalPremium: 185000,
        totalFYC: 35200,
      },
      tierAchieved: "Gold Elite",
      rewards: [
        { type: "monetary", amount: 10000, currency: "HKD" },
        { type: "non-monetary", perk: "thailand_trip" },
      ],
    },
    {
      agentId: "AG002",
      agentName: "林志偉",
      englishName: "Wilson Lam",
      metrics: {
        totalPremium: 178000,
        totalFYC: 33600,
      },
      tierAchieved: "Gold Elite",
      rewards: [
        { type: "monetary", amount: 10000, currency: "HKD" },
        { type: "non-monetary", perk: "thailand_trip" },
      ],
    },
    // Silver Champion (3 agents - 20.0%)
    {
      agentId: "AG003",
      agentName: "李小芳",
      englishName: "Fanny Li",
      metrics: {
        totalPremium: 142000,
        totalFYC: 28400,
      },
      tierAchieved: "Silver Champion",
      rewards: [{ type: "monetary", amount: 5000, currency: "HKD" }],
    },
    {
      agentId: "AG004",
      agentName: "周美華",
      englishName: "Megan Chow",
      metrics: {
        totalPremium: 138000,
        totalFYC: 27600,
      },
      tierAchieved: "Silver Champion",
      rewards: [{ type: "monetary", amount: 5000, currency: "HKD" }],
    },
    {
      agentId: "AG005",
      agentName: "黃志明",
      englishName: "Ming Wong",
      metrics: {
        totalPremium: 135000,
        totalFYC: 27000,
      },
      tierAchieved: "Silver Champion",
      rewards: [{ type: "monetary", amount: 5000, currency: "HKD" }],
    },
    // Bronze Star (5 agents - 33.3%)
    {
      agentId: "AG006",
      agentName: "王志強",
      englishName: "Strong Wang",
      metrics: {
        totalPremium: 98000,
        totalFYC: 18200,
      },
      tierAchieved: "Bronze Star",
      rewards: [{ type: "monetary", amount: 2000, currency: "HKD" }],
    },
    {
      agentId: "AG007",
      agentName: "劉小玲",
      englishName: "Ling Liu",
      metrics: {
        totalPremium: 95000,
        totalFYC: 17500,
      },
      tierAchieved: "Bronze Star",
      rewards: [{ type: "monetary", amount: 2000, currency: "HKD" }],
    },
    {
      agentId: "AG008",
      agentName: "陳家豪",
      englishName: "Ho Chan",
      metrics: {
        totalPremium: 92000,
        totalFYC: 17000,
      },
      tierAchieved: "Bronze Star",
      rewards: [{ type: "monetary", amount: 2000, currency: "HKD" }],
    },
    {
      agentId: "AG009",
      agentName: "鄭雅文",
      englishName: "Aman Cheng",
      metrics: {
        totalPremium: 88000,
        totalFYC: 16500,
      },
      tierAchieved: "Bronze Star",
      rewards: [{ type: "monetary", amount: 2000, currency: "HKD" }],
    },
    {
      agentId: "AG010",
      agentName: "吳國強",
      englishName: "Strong Ng",
      metrics: {
        totalPremium: 85000,
        totalFYC: 16000,
      },
      tierAchieved: "Bronze Star",
      rewards: [{ type: "monetary", amount: 2000, currency: "HKD" }],
    },
    // No Reward (5 agents - 33.3%)
    {
      agentId: "AG011",
      agentName: "陳美麗",
      englishName: "Mary Chan",
      metrics: {
        totalPremium: 65000,
        totalFYC: 12500,
      },
      tierAchieved: "No Reward",
      rewards: [],
    },
    {
      agentId: "AG012",
      agentName: "楊小明",
      englishName: "Ming Yang",
      metrics: {
        totalPremium: 58000,
        totalFYC: 11000,
      },
      tierAchieved: "No Reward",
      rewards: [],
    },
    {
      agentId: "AG013",
      agentName: "許志華",
      englishName: "Wah Hui",
      metrics: {
        totalPremium: 52000,
        totalFYC: 10200,
      },
      tierAchieved: "No Reward",
      rewards: [],
    },
    {
      agentId: "AG014",
      agentName: "馮小紅",
      englishName: "Red Fung",
      metrics: {
        totalPremium: 48000,
        totalFYC: 9500,
      },
      tierAchieved: "No Reward",
      rewards: [],
    },
    {
      agentId: "AG015",
      agentName: "譚志偉",
      englishName: "David Tam",
      metrics: {
        totalPremium: 42000,
        totalFYC: 8800,
      },
      tierAchieved: "No Reward",
      rewards: [],
    },
  ],
};

const PERK_LABELS = {
  thailand_trip: "Thailand Trip",
  iphone_latest: "Latest iPhone",
  macbook_pro: "MacBook Pro",
  tesla_model3: "Tesla Model 3",
  vip_dining: "VIP Dining Experience",
  spa_weekend: "Spa Weekend",
  golf_membership: "Golf Club Membership",
  wine_collection: "Premium Wine Collection",
};

const DashboardWidget = ({ title, value, subtitle, icon, color = "blue" }) => {
  return (
    <Card shadow="xs" padding="md" radius="md" withBorder>
      <Group position="apart">
        <div>
          <Text color="dimmed" size="sm" transform="uppercase" weight={700}>
            {title}
          </Text>
          <Text weight={700} size="xl">
            {value}
          </Text>
          {subtitle && (
            <Text color="dimmed" size="xs">
              {subtitle}
            </Text>
          )}
        </div>
        <ActionIcon size={40} radius="md" color={color} variant="light">
          {icon}
        </ActionIcon>
      </Group>
    </Card>
  );
};

const TierStatsWidget = ({ tierStats, totalAgents }) => {
  const [formatter] = useFormatter();

  return (
    <Card shadow="xs" padding="md" radius="md" withBorder>
      <Group position="apart" mb="md">
        <Text weight={500} size="lg">
          Reward Tier Achievement
        </Text>
        <ActionIcon variant="light" color="blue">
          <IconChartPie size={20} />
        </ActionIcon>
      </Group>

      <Stack spacing="md">
        {tierStats.map((tier, index) => (
          <div key={index}>
            <Group position="apart" mb={4}>
              <Text size="sm" weight={500}>
                {tier.tierName}
              </Text>
              <Text size="sm" color="dimmed">
                {tier.achieved} agents ({tier.percentage}%)
              </Text>
            </Group>
            <Progress
              value={tier.percentage}
              size="lg"
              radius="xl"
              color={tier.color}
            />
          </div>
        ))}
      </Stack>

      {/* <Divider my="md" /> */}
      {/* 
      <Center>
        <RingProgress
          size={120}
          thickness={8}
          sections={tierStats
            .filter((tier) => tier.tierName !== "No Reward")
            .map((tier) => ({
              value: tier.percentage,
              color: tier.color,
              tooltip: `${tier.tierName}: ${tier.achieved} agents`,
            }))}
          label={
            <Text color="blue" weight={700} align="center" size="xs">
              {(
                ((totalAgents -
                  tierStats.find((t) => t.tierName === "No Reward")?.achieved ||
                  0) /
                  totalAgents) *
                100
              ).toFixed(1)}
              %<br />
              <Text size="xs" color="dimmed">
                Rewarded
              </Text>
            </Text>
          }
        />
      </Center> */}
    </Card>
  );
};

const AgentResultsTable = ({ agentResults }) => {
  const [formatter] = useFormatter();

  const getTierBadgeColor = (tier) => {
    switch (tier) {
      case "Gold Elite":
        return "yellow";
      case "Silver Champion":
        return "gray";
      case "Bronze Star":
        return "orange";
      default:
        return "red";
    }
  };

  const formatRewards = (rewards) => {
    if (!rewards || rewards.length === 0) return "No reward";

    return rewards
      .map((reward) => {
        if (reward.type === "monetary") {
          return `${formatter.currency(reward.amount, reward.currency)}`;
        } else {
          return PERK_LABELS[reward.perk] || reward.perk;
        }
      })
      .join(", ");
  };

  return (
    <Paper withBorder p="md">
      <Group position="apart" mb="md">
        <Text weight={500} size="lg">
          Agent Results
        </Text>
        <Group>
          <Badge size="sm" variant="light">
            {agentResults.length} agents
          </Badge>
        </Group>
      </Group>

      <Box style={{ overflowX: "auto" }}>
        <Table fontSize="sm" striped highlightOnHover>
          <thead>
            <tr>
              <th>Agent</th>
              <th>Total Premium</th>
              <th>Total FYC</th>
              <th>Tier Achieved</th>
              <th>Rewards</th>
            </tr>
          </thead>
          <tbody>
            {agentResults.map((agent, index) => (
              <tr key={index}>
                <td>
                  <div>
                    <Text size="sm" weight={500}>
                      {agent.agentName}
                    </Text>
                    <Text size="xs" color="dimmed">
                      {agent.englishName} ({agent.agentId})
                    </Text>
                  </div>
                </td>
                <td>
                  <Text size="sm">
                    {formatter.currency(agent.metrics.totalPremium)}
                  </Text>
                </td>
                <td>
                  <Text size="sm">
                    {formatter.currency(agent.metrics.totalFYC)}
                  </Text>
                </td>
                <td>
                  <Badge
                    size="sm"
                    color={getTierBadgeColor(agent.tierAchieved)}
                    variant="light"
                  >
                    {agent.tierAchieved}
                  </Badge>
                </td>
                <td>
                  <Text size="xs" lineClamp={2}>
                    {formatRewards(agent.rewards)}
                  </Text>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Box>
    </Paper>
  );
};

const CompensationResult = ({ form, name, ...props }) => {
  const [formatter] = useFormatter();
  const [isRunning, setIsRunning] = useState(false);
  const [hasResults, setHasResults] = useState(false); // Hidden until run button is clicked
  const [isExporting, setIsExporting] = useState(false);

  // Get form data
  const campaignData = form?.values || {};
  const campaignStatus = campaignData.status || "ACTIVE";
  const isEnded = campaignStatus === "EXPIRED";

  const handleRunCampaign = async () => {
    setIsRunning(true);
    // Simulate API call
    setTimeout(() => {
      setIsRunning(false);
      setHasResults(true);
    }, 2000);
  };

  const handleDownloadReport = async () => {
    setIsExporting(true);
    // Simulate download
    setTimeout(() => {
      setIsExporting(false);
      // In real implementation, trigger file download
      console.log("Downloading report...");
    }, 1000);
  };

  const handlePrintReport = async () => {
    // In real implementation, open print dialog or generate PDF
    window.print();
  };

  const handleDispatchReward = async () => {
    if (!isEnded) return;
    // In real implementation, dispatch rewards to agents
    console.log("Dispatching rewards...");
  };

  return (
    <Stack spacing="xl">
      {/* Action Buttons */}
      <Paper withBorder p="md">
        <Group position="apart">
          <Group>
            <Button
              leftIcon={<IconRefresh size={16} />}
              onClick={handleRunCampaign}
              loading={isRunning}
              color="blue"
              size="sm"
            >
              {isRunning ? "Running..." : "Run Campaign"}
            </Button>

            {hasResults && (
              <Text size="xs" color="dimmed">
                Last run: {moment(DUMMY_CAMPAIGN_DATA.runDate).format("LLL")}
              </Text>
            )}
          </Group>

          <Group>
            <Button
              leftIcon={<IconDownload size={16} />}
              onClick={handleDownloadReport}
              loading={isExporting}
              //   variant="outline"
              size="sm"
              disabled={!hasResults}
            >
              Download Report
            </Button>

            <Button
              leftIcon={<IconPrinter size={16} />}
              onClick={handlePrintReport}
              //   variant="outline"
              size="sm"
              disabled={!hasResults}
            >
              Print
            </Button>

            <Button
              leftIcon={<IconGift size={16} />}
              onClick={handleDispatchReward}
              color="green"
              size="sm"
              disabled={!hasResults || !isEnded}
            >
              Dispatch Rewards
            </Button>
          </Group>
        </Group>

        {!isEnded && (
          <Text size="xs" color="orange" mt="xs">
            ⚠️ Reward dispatch will be enabled when campaign ends
          </Text>
        )}
      </Paper>

      {/* Loading Overlay */}
      <Box style={{ position: "relative", minHeight: hasResults ? 0 : 200 }}>
        <LoadingOverlay visible={isRunning} overlayBlur={2} />

        {!hasResults && !isRunning && (
          <Center style={{ minHeight: 200 }}>
            <Stack align="center" spacing="md">
              {/* <IconPlaylist size={48} color="gray" /> */}
              <Text color="dimmed" align="center">
                Click "Run Campaign" to calculate results
              </Text>
            </Stack>
          </Center>
        )}

        {hasResults && (
          <Stack spacing="xl">
            {/* Dashboard Widgets */}
            <div>
              <Text weight={500} size="lg" mb="md">
                Campaign Overview
              </Text>
              <SimpleGrid
                cols={4}
                breakpoints={[
                  { maxWidth: "md", cols: 2 },
                  { maxWidth: "sm", cols: 1 },
                ]}
              >
                <DashboardWidget
                  title="Total Agents"
                  value={DUMMY_CAMPAIGN_DATA.totalAgents}
                  subtitle="Participants"
                  icon={<IconUsers size={24} />}
                  color="gray"
                />
                <DashboardWidget
                  title="Total Premium"
                  value={formatter.currency(
                    DUMMY_CAMPAIGN_DATA.metrics.totalPremium
                  )}
                  subtitle="Generated"
                  icon={<IconChartBar size={24} />}
                  color="gray"
                />
                <DashboardWidget
                  title="Total FYC"
                  value={formatter.currency(
                    DUMMY_CAMPAIGN_DATA.metrics.totalFYC
                  )}
                  subtitle="Earned"
                  icon={<IconTrophy size={24} />}
                  color="gray"
                />
                <DashboardWidget
                  title="Average per Agent"
                  value={formatter.currency(
                    DUMMY_CAMPAIGN_DATA.metrics.averagePerAgent
                  )}
                  subtitle="Performance"
                  icon={<IconChartPie size={24} />}
                  color="gray"
                />
              </SimpleGrid>
            </div>

            {/* Tier Statistics */}
            <div>
              <Text weight={500} size="lg" mb="md">
                Reward Achievement Statistics
              </Text>
              <TierStatsWidget
                tierStats={DUMMY_CAMPAIGN_DATA.tierStats}
                totalAgents={DUMMY_CAMPAIGN_DATA.totalAgents}
              />
            </div>

            {/* Summary Stats */}
            <Paper withBorder p="md">
              <Text weight={500} size="lg" mb="md">
                Reward Summary
              </Text>
              <Grid>
                <Grid.Col span={3}>
                  <Text size="sm" color="dimmed">
                    Total Monetary Rewards
                  </Text>
                  <Text weight={700} size="lg">
                    {formatter.currency(
                      DUMMY_CAMPAIGN_DATA.agentResults.reduce((sum, agent) => {
                        return (
                          sum +
                          agent.rewards
                            .filter((r) => r.type === "monetary")
                            .reduce((rSum, r) => rSum + r.amount, 0)
                        );
                      }, 0)
                    )}
                  </Text>
                </Grid.Col>
                <Grid.Col span={3}>
                  <Text size="sm" color="dimmed">
                    Non-Monetary Rewards
                  </Text>
                  <Text weight={700} size="lg">
                    {DUMMY_CAMPAIGN_DATA.agentResults.reduce((sum, agent) => {
                      return (
                        sum +
                        agent.rewards.filter((r) => r.type === "non-monetary")
                          .length
                      );
                    }, 0)}{" "}
                    items
                  </Text>
                </Grid.Col>
                <Grid.Col span={3}>
                  <Text size="sm" color="dimmed">
                    Agents Rewarded
                  </Text>
                  <Text weight={700} size="lg">
                    {
                      DUMMY_CAMPAIGN_DATA.agentResults.filter(
                        (agent) => agent.rewards.length > 0
                      ).length
                    }{" "}
                    /{DUMMY_CAMPAIGN_DATA.totalAgents}
                  </Text>
                </Grid.Col>
                <Grid.Col span={3}>
                  <Text size="sm" color="dimmed">
                    Success Rate
                  </Text>
                  <Text weight={700} size="lg">
                    {(
                      (DUMMY_CAMPAIGN_DATA.agentResults.filter(
                        (agent) => agent.rewards.length > 0
                      ).length /
                        DUMMY_CAMPAIGN_DATA.totalAgents) *
                      100
                    ).toFixed(1)}
                    %
                  </Text>
                </Grid.Col>
              </Grid>
            </Paper>

            {/* Agent Results Table */}
            <div>
              <AgentResultsTable
                agentResults={DUMMY_CAMPAIGN_DATA.agentResults}
              />
            </div>
          </Stack>
        )}
      </Box>
    </Stack>
  );
};

export default CompensationResult;
